// src/utils/tagHelpers.ts
export type GroupedTags = {
  cost: 1 | 2 | 3 | null;     // 1 = cheap, 2 = mid, 3 = expensive
  spicy: 1 | 2 | 3 | 4 | null; // 1 = none, 2 = mild, 3 = medium, 4 = hot
  showQuick: boolean;
  showEasy: boolean;
  dietary: string[];           // now exactly 0 or 1
  nutrition: string[];
  mealType: string[];
  occasion: string[];
  season: string[];
  all: string[];
};

const DIET_PRIORITY = [
  'vegan',
  'vegetarian',
  'pescetarian',
  'keto_friendly',
  'gluten_free',
  'lactose_free',
];

const MEAL_TYPES = [
  'dessert', 'breakfast', 'brunch', 'lunch', 'dinner',
  'snack', 'appetizer', 'side', 'stew', 'dip', 'soup',
];

const SEASONS = [
  'summer', 'spring', 'holiday', 'christmas',
  'thanksgiving', 'winter', 'fall',
];

const OCCASIONS = [
  'comfort', 'party', 'date_night', 'romantic',
  'family_favorite', 'kid_friendly',
];

const NUTRITION_TAGS = [
  'high_protein', 'high_fiber', 'low_carb',
  'low_fat', 'low_sugar', 'low_calorie',
];

export function classifyTags(tags: string[]): GroupedTags {
  const lower = tags.map((t) => t.toLowerCase());
  const tagSet = new Set(lower);

  const result: GroupedTags = {
    cost: null,
    spicy: null,
    showQuick: tagSet.has('quick'),
    showEasy: tagSet.has('easy'),
    dietary: [],
    nutrition: [],
    mealType: [],
    occasion: [],
    season: [],
    all: tags,
  };

  // Cost
  if (tagSet.has('cheap')) result.cost = 1;
  else if (tagSet.has('mid_range')) result.cost = 2;
  else if (tagSet.has('expensive')) result.cost = 3;

  // Spicy
  if (tagSet.has('none')) result.spicy = 1;
  else if (tagSet.has('mild')) result.spicy = 2;
  else if (tagSet.has('medium')) result.spicy = 3;
  else if (tagSet.has('hot')) result.spicy = 4;

  // Dietary: pick exactly one in priority order
  for (const diet of DIET_PRIORITY) {
    if (tagSet.has(diet)) {
      result.dietary.push(diet);
      break;
    }
  }

  // Nutrition (up to all; limit happens in UI)
  for (const nut of NUTRITION_TAGS) {
    if (tagSet.has(nut)) result.nutrition.push(nut);
  }

  // Meal Type
  for (const m of MEAL_TYPES) {
    if (tagSet.has(m)) result.mealType.push(m);
  }

  // Occasion
  for (const o of OCCASIONS) {
    if (tagSet.has(o)) result.occasion.push(o);
  }

  // Season
  for (const s of SEASONS) {
    if (tagSet.has(s)) result.season.push(s);
  }

  return result;
}
